import { useRef } from 'react';

import { Typography } from '@braskai/ui-kit';
import { useClickAway } from 'react-use';

import { CardMedia } from '@mui/material';
import Lottie from 'lottie-react';

import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { useInterfaceLanguage } from '@/shared/i18n';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { OnboardingStepKeys } from '@/shared/types/Onboarding';

import { getOnboardingSteps } from '../../lib';
import { SButton, STooltipActions, STooltipContent, STooltipTitle, STooltipWrapper } from './styled';

type OnboardingPopperProps = {
  step: OnboardingStepKeys;
  onClose: () => void;
};

export const OnboardingPopper = ({ step, onClose }: OnboardingPopperProps) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const { t } = useInterfaceLanguage();

  const {
    title,
    actions = [],
    animationData,
    imagePlacement = 'bottom',
    imageSrc,
    minHeight,
    subtitle,
    width,
    onRender,
  } = getOnboardingSteps(t)[step];

  const image = imageSrc && (
    <CardMedia alt="" component="img" src={imageSrc} style={imagePlacement === 'top' ? { borderRadius: 16 } : {}} />
  );

  const handleClickAway = () => {
    onClose();
    MixpanelEvents.onboardingExitClick();
  };

  useClickAway(tooltipRef, handleClickAway);

  useExecuteOneTime(() => {
    onRender?.();
  });

  return (
    <STooltipWrapper minHeight={minHeight} ref={tooltipRef} width={width}>
      {imagePlacement === 'top' && image}
      <STooltipTitle display="flex" flexDirection="column" gap={4}>
        {subtitle ? (
          <Typography color="grey.900" variant="caption-1-md">
            {title}
          </Typography>
        ) : (
          title
        )}
        {subtitle && (
          <Typography color="grey.700" variant="caption-1">
            {subtitle}
          </Typography>
        )}
      </STooltipTitle>

      {animationData ? (
        <STooltipContent>
          <Lottie animationData={animationData} loop />
        </STooltipContent>
      ) : null}
      {imagePlacement === 'bottom' && image}
      <STooltipActions>
        {actions.map((action) => (
          <SButton
            key={action.id}
            $isLink={action.type === 'link'}
            data-testid="onboarding-popper-button"
            size="xs"
            variant="text"
            onClick={() => {
              onClose();
              action.handler?.();
            }}
          >
            {action.title}
          </SButton>
        ))}
      </STooltipActions>
    </STooltipWrapper>
  );
};
